import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquare, Mic, Phone, Send, Square, PhoneOutgoing } from "lucide-react";
import ProductCards from "./ProductCards";

const CHANNELS = [
  { id: "web_chat", label: "Web Chat", Icon: MessageSquare },
  { id: "web_voice", label: "Web Voice", Icon: Mic },
];

export default function SimulatorColumn({ agentId, channels }) {
  const enabled = (c) => channels.includes(c);
  const firstEnabled = CHANNELS.find((c) => enabled(c.id))?.id;
  const [tab, setTab] = useState(firstEnabled || "web_chat");
  useEffect(() => { if (firstEnabled) setTab(firstEnabled); /* eslint-disable-next-line */ }, [channels.join(",")]);

  // Chat state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [convId, setConvId] = useState(null);
  const [thinking, setThinking] = useState(false);
  const scrollRef = useRef(null);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }); }, [messages, thinking]);

  const sendChat = async () => {
    if (!input.trim()) return;
    const u = input.trim();
    setMessages((m) => [...m, { role: "user", content: u }]);
    setInput(""); setThinking(true);
    try {
      const { data } = await api.post("/chat/message", { agent_id: agentId, message: u, conversation_id: convId });
      setConvId(data.conversation_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply, sources: data.sources, products: data.products }]);
    } catch (err) { toast.error(err?.response?.data?.detail || "Chat failed"); }
    finally { setThinking(false); }
  };

  // Voice state (Cascade pipeline: MediaRecorder → STT → LLM → TTS) with continuous VAD
  const [voiceStatus, setVoiceStatus] = useState("idle"); // idle | listening | recording | thinking | speaking
  const [voiceTurns, setVoiceTurns] = useState([]);
  const [audioLevel, setAudioLevel] = useState(0);
  const conversationActiveRef = useRef(false);
  const greetedRef = useRef(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const localStreamRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const voiceStartRef = useRef(null);
  const voiceConvIdRef = useRef(null);
  const channelRef = useRef("web_voice");
  const turnsScrollRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const vadFrameRef = useRef(null);
  const lastVoiceAtRef = useRef(0);
  const recStartedAtRef = useRef(0);
  useEffect(() => { turnsScrollRef.current?.scrollTo({ top: turnsScrollRef.current.scrollHeight }); }, [voiceTurns]);

  const SPEECH_THRESHOLD = 18;
  const SILENCE_HOLD_MS = 1300;
  const MIN_UTTERANCE_MS = 400;
  const MAX_UTTERANCE_MS = 30000;

  const teardownAudio = () => {
    try { vadFrameRef.current && cancelAnimationFrame(vadFrameRef.current); } catch { /* noop */ }
    vadFrameRef.current = null;
    try { mediaRecorderRef.current?.state !== "inactive" && mediaRecorderRef.current?.stop(); } catch { /* noop */ }
    mediaRecorderRef.current = null;
    try { localStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    localStreamRef.current = null;
    try { audioCtxRef.current?.close(); } catch { /* noop */ }
    audioCtxRef.current = null;
    analyserRef.current = null;
    try { audioPlayerRef.current?.pause(); } catch { /* noop */ }
    audioPlayerRef.current = null;
    setAudioLevel(0);
  };

  const startVoice = async (forPhone = false) => {
    if (voiceStatus !== "idle" && voiceStatus !== "speaking") return;
    channelRef.current = forPhone ? "phone-preview" : "web_voice";
    voiceStartRef.current = voiceStartRef.current || Date.now();
    return _openMicAndListen(forPhone);
  };

  const _openMicAndListen = async (forPhone = false) => {
    conversationActiveRef.current = true;
    setVoiceStatus("listening");
    try {
      const ms = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      localStreamRef.current = ms;
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      audioCtxRef.current = ctx;
      const src = ctx.createMediaStreamSource(ms);
      const analyser = ctx.createAnalyser(); analyser.fftSize = 512;
      src.connect(analyser);
      analyserRef.current = analyser;
      runVadLoop();
      if (forPhone) toast.success("Phone preview live — just speak.");
    } catch (err) {
      conversationActiveRef.current = false;
      toast.error(err.message || "Mic access denied");
      setVoiceStatus("idle");
    }
  };

  const autoGreet = async () => {
    setVoiceStatus("thinking");
    let data;
    try {
      const res = await api.post("/voice/greet-public", { agent_id: agentId });
      data = res.data;
    } catch {
      setVoiceStatus("idle");
      return;
    }
    setVoiceTurns((t) => [...t, { role: "assistant", content: data.text, ts: Date.now() }]);
    try {
      const audio = new Audio(`data:${data.audio_mime || "audio/mpeg"};base64,${data.audio_base64}`);
      audioPlayerRef.current = audio;
      setVoiceStatus("speaking");
      audio.onended = () => { _openMicAndListen(); };
      audio.onerror = () => { _openMicAndListen(); };
      await audio.play();
    } catch {
      _openMicAndListen();
    }
  };

  const beginUtterance = () => {
    const ms = localStreamRef.current;
    if (!ms) return;
    const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
    const rec = new MediaRecorder(ms, { mimeType: mime });
    mediaRecorderRef.current = rec;
    audioChunksRef.current = [];
    rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data); };
    rec.onstop = () => {
      const blob = new Blob(audioChunksRef.current, { type: mime });
      audioChunksRef.current = [];
      const duration = Date.now() - recStartedAtRef.current;
      if (duration < MIN_UTTERANCE_MS || blob.size < 1500) {
        if (conversationActiveRef.current) { setVoiceStatus("listening"); runVadLoop(); }
        return;
      }
      sendCascadeTurn(blob, mime);
    };
    recStartedAtRef.current = Date.now();
    rec.start();
    setVoiceStatus("recording");
  };

  const runVadLoop = () => {
    const analyser = analyserRef.current;
    if (!analyser || !conversationActiveRef.current) return;
    const data = new Uint8Array(analyser.frequencyBinCount);
    let recording = false;
    lastVoiceAtRef.current = 0;
    const tick = () => {
      if (!conversationActiveRef.current || !analyserRef.current) return;
      analyser.getByteFrequencyData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) sum += data[i];
      const avg = sum / data.length;
      setAudioLevel(Math.min(1, avg / 90));
      const now = Date.now();
      const isSpeech = avg > SPEECH_THRESHOLD;
      if (!recording) {
        if (isSpeech) {
          recording = true;
          beginUtterance();
          lastVoiceAtRef.current = now;
        }
      } else {
        if (isSpeech) lastVoiceAtRef.current = now;
        const sinceVoice = now - lastVoiceAtRef.current;
        const elapsed = now - recStartedAtRef.current;
        if (sinceVoice > SILENCE_HOLD_MS || elapsed > MAX_UTTERANCE_MS) {
          recording = false;
          try { mediaRecorderRef.current?.stop(); } catch { /* noop */ }
          return;
        }
      }
      vadFrameRef.current = requestAnimationFrame(tick);
    };
    vadFrameRef.current = requestAnimationFrame(tick);
  };

  const sendCascadeTurn = async (blob, mime) => {
    setVoiceStatus("thinking");
    const fd = new FormData();
    const ext = mime.includes("webm") ? "webm" : "mp4";
    fd.append("audio", blob, `voice.${ext}`);
    fd.append("agent_id", agentId);
    if (voiceConvIdRef.current) fd.append("conversation_id", voiceConvIdRef.current);
    let data;
    try {
      const res = await api.post("/voice/cascade-turn", fd, { headers: { "Content-Type": "multipart/form-data" } });
      data = res.data;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Voice turn failed");
      if (conversationActiveRef.current) { setVoiceStatus("listening"); runVadLoop(); }
      else setVoiceStatus("idle");
      return;
    }
    voiceConvIdRef.current = data.conversation_id;
    setVoiceTurns((t) => [
      ...t,
      { role: "user", content: data.user_text, ts: Date.now() },
      { role: "assistant", content: data.assistant_text, products: data.products, ts: Date.now() },
    ]);
    try {
      const audio = new Audio(`data:${data.audio_mime || "audio/mpeg"};base64,${data.audio_base64}`);
      audioPlayerRef.current = audio;
      setVoiceStatus("speaking");
      const resume = () => {
        if (conversationActiveRef.current) { setVoiceStatus("listening"); runVadLoop(); }
        else setVoiceStatus("idle");
      };
      audio.onended = resume;
      audio.onerror = resume;
      await audio.play();
    } catch {
      if (conversationActiveRef.current) { setVoiceStatus("listening"); runVadLoop(); }
      else setVoiceStatus("idle");
    }
  };

  const stopVoice = () => {
    conversationActiveRef.current = false;
    teardownAudio();
    setVoiceStatus("idle");
  };
  useEffect(() => () => stopVoice(), []); // cleanup on unmount

  // Auto-stop voice when user switches away from a voice tab
  useEffect(() => {
    if (tab !== "web_voice" && voiceStatus !== "idle") {
      stopVoice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Stop voice if page becomes hidden (tab change / minimize)
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden && voiceStatus !== "idle") stopVoice();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceStatus]);

  // Auto-greet when the user opens the Web Voice tab for the first time.
  // Phone preview keeps the manual "tap to call" since it simulates a real PSTN dial.
  useEffect(() => {
    if (tab !== "web_voice" || greetedRef.current || voiceStatus !== "idle") return;
    greetedRef.current = true;
    autoGreet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const visibleTabs = CHANNELS.filter((c) => enabled(c.id));

  return (
    <div className="space-y-3" data-testid="builder-col-simulator">
      <div className="flex items-center gap-2 pb-2 border-b-2 border-[#030712]">
        <div className="font-display font-extrabold text-lg uppercase tracking-tight">Simulator</div>
        <div className="text-[10px] font-mono text-[#4B5563]">live test sandbox</div>
      </div>

      <div className="hs-card h-[640px] flex flex-col">
        <div className="border-b border-[#E2E8F0] flex">
          {visibleTabs.length === 0 && <div className="px-3 py-3 text-xs text-[#4B5563]">Enable a channel to simulate</div>}
          {visibleTabs.map(({ id, label, Icon }) => (
            <button
              key={id}
              data-testid={`tester-tab-${id}`}
              onClick={() => setTab(id)}
              className={`flex-1 px-2 py-3 text-[11px] font-medium flex items-center justify-center gap-1.5 border-b-2 ${tab === id ? "border-[#0044FF] text-[#030712]" : "border-transparent text-[#4B5563]"}`}
            >
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        {tab === "web_chat" && enabled("web_chat") && (
          <>
            <div ref={scrollRef} className="flex-1 overflow-auto p-4 space-y-3 text-sm" data-testid="builder-chat-messages">
              {messages.length === 0 && <div className="text-xs text-[#4B5563] text-center mt-12">Send a message to test your agent.</div>}
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] ${m.role === "user" ? "bg-[#0044FF] text-white" : "bg-[#F3F4F6]"} px-3 py-2 rounded-sm text-sm whitespace-pre-wrap`}>
                    {m.content}
                    {m.products?.length > 0 && <ProductCards products={m.products} compact />}
                    {m.sources?.length > 0 && <div className="text-[10px] font-mono mt-2 opacity-70 border-t border-current/20 pt-1.5">{Array.from(new Set(m.sources.map((s) => s.source))).slice(0, 3).join(" · ")}</div>}
                  </div>
                </div>
              ))}
              {thinking && <div className="text-xs text-[#4B5563]">thinking…</div>}
            </div>
            <div className="p-3 border-t border-[#E2E8F0] flex gap-2">
              <input data-testid="builder-chat-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendChat()} placeholder="Ask your agent something…" className="flex-1 border border-[#E2E8F0] px-3 py-2 rounded-sm text-sm" />
              <button onClick={sendChat} disabled={thinking || !input.trim()} className="btn-primary text-sm" data-testid="builder-chat-send-button"><Send className="w-4 h-4" /></button>
            </div>
          </>
        )}

        {(tab === "web_voice" && enabled("web_voice")) && (
          <VoicePane mode="voice" voiceStatus={voiceStatus} startVoice={() => startVoice(false)} stopVoice={stopVoice} voiceTurns={voiceTurns} audioLevel={audioLevel} turnsScrollRef={turnsScrollRef} />
        )}

        {!enabled(tab) && visibleTabs.length > 0 && (
          <div className="flex-1 flex items-center justify-center text-sm text-[#4B5563] p-6 text-center">Select an enabled channel above.</div>
        )}
      </div>
    </div>
  );
}

function VoicePane({ mode, voiceStatus, startVoice, stopVoice, voiceTurns, audioLevel, turnsScrollRef }) {
  const idle = voiceStatus === "idle";
  const active = voiceStatus !== "idle";
  const recording = voiceStatus === "recording";
  const listening = voiceStatus === "listening";
  const busy = voiceStatus === "thinking" || voiceStatus === "speaking";
  const pulse = Math.max(0.4, Math.min(1, 0.4 + audioLevel * 0.8));
  return (
    <div className="flex-1 flex flex-col relative bg-gradient-to-b from-[#030712] to-[#0B1220] text-white overflow-hidden">
      {/* Animated radial glow */}
      {(recording || listening) && (
        <div className="absolute inset-0 pointer-events-none" aria-hidden>
          <div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{
              width: 320, height: 320,
              background: `radial-gradient(circle, ${recording ? "rgba(239,68,68,0.25)" : "rgba(0,68,255,0.18)"} 0%, transparent 70%)`,
              transform: `translate(-50%, -50%) scale(${pulse})`,
              transition: "transform 90ms linear",
              filter: "blur(8px)",
            }}
          />
        </div>
      )}

      {/* Header strip */}
      <div className="relative px-4 py-2.5 border-b border-white/10 flex items-center justify-between text-[11px] font-mono">
        <div className="flex items-center gap-2">
          {mode === "phone" ? <PhoneOutgoing className="w-3.5 h-3.5 text-[#0044FF]" /> : <Mic className="w-3.5 h-3.5 text-[#0044FF]" />}
          {mode === "phone" ? "phone preview · hands-free" : "cascade voice · hands-free"}
        </div>
        <div className={`px-2 py-0.5 rounded-sm ${recording ? "bg-[#EF4444]/20 text-[#EF4444]" : listening ? "bg-[#10B981]/20 text-[#10B981]" : busy ? "bg-[#F59E0B]/20 text-[#F59E0B]" : "bg-white/5 text-white/60"}`}>
          {voiceStatus}
        </div>
      </div>

      {/* Transcript timeline */}
      <div ref={turnsScrollRef} className="flex-1 overflow-auto relative p-4 space-y-2.5" data-testid="voice-timeline">
        {voiceTurns.length === 0 && idle && (
          <div className="text-center text-white/40 text-xs mt-16">
            <div className="font-mono text-[10px] uppercase tracking-widest">tap the mic and just talk</div>
            <div className="text-[11px] mt-2 max-w-xs mx-auto">No buttons after that. I&apos;ll listen, reply, and keep listening. Try: <span className="text-white/70">&quot;what do you have under $50?&quot;</span></div>
          </div>
        )}
        {voiceTurns.length === 0 && listening && (
          <div className="text-center text-white/40 text-xs mt-16">
            <div className="font-mono text-[10px] uppercase tracking-widest animate-pulse">listening — go ahead</div>
          </div>
        )}
        {voiceTurns.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[88%] px-3 py-2 rounded-sm text-sm ${m.role === "user" ? "bg-[#0044FF] text-white" : "bg-white/10 backdrop-blur-sm text-white"}`}>
              <div className="text-[9px] font-mono uppercase tracking-widest opacity-60 mb-1">{m.role === "user" ? "YOU" : "ASSISTANT"}</div>
              {m.content && <div className="whitespace-pre-wrap">{m.content}</div>}
              {m.products?.length > 0 && (
                <div className="-mx-1 mt-2">
                  <ProductCards products={m.products} compact />
                </div>
              )}
            </div>
          </div>
        ))}
        {busy && voiceTurns.length > 0 && (
          <div className="text-[10px] font-mono text-white/50 px-1 animate-pulse">{voiceStatus === "thinking" ? "thinking…" : "speaking…"}</div>
        )}
      </div>

      {/* Single toggle — start / end conversation */}
      <div className="relative p-4 flex flex-col items-center justify-center gap-2">
        {idle ? (
          <button
            data-testid={mode === "phone" ? "tester-phone-start" : "tester-voice-start"}
            onClick={startVoice}
            className="relative w-16 h-16 rounded-full bg-[#0044FF] hover:bg-[#0033CC] text-white flex items-center justify-center transition-all shadow-lg shadow-[#0044FF]/40"
          >
            {mode === "phone" ? <Phone className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
            <span className="absolute -inset-1 rounded-full border border-[#0044FF]/40 animate-ping" />
          </button>
        ) : (
          <button
            data-testid={mode === "phone" ? "tester-phone-stop" : "tester-voice-stop"}
            onClick={stopVoice}
            className={`relative w-16 h-16 rounded-full text-white flex items-center justify-center transition-all shadow-lg ${
              recording ? "bg-[#EF4444] shadow-[#EF4444]/30 animate-pulse" :
              listening ? "bg-[#10B981] shadow-[#10B981]/30" :
              "bg-white/10"
            }`}
          >
            {busy ? (
              <div className="w-6 h-6 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            ) : (
              <Square className="w-6 h-6" />
            )}
            {(recording || listening) && (
              <span
                className="absolute -inset-2 rounded-full border border-white/30"
                style={{ transform: `scale(${pulse})`, transition: "transform 90ms linear" }}
              />
            )}
          </button>
        )}
        <div className="text-[9px] font-mono uppercase tracking-widest text-white/40">
          {idle ? "tap to start" :
           listening ? "listening · tap to end" :
           recording ? "you're speaking…" :
           voiceStatus === "thinking" ? "thinking…" :
           "assistant speaking…"}
        </div>
      </div>
    </div>
  );
}
